// doctor-frontend/src/pages/intern/GuidelinesLibrary.jsx
// Enhanced (no regression):
// - Supports deep link ?open=GL-CODE to highlight + focus that guideline
// - Keeps ?q= search param behavior
// - Adds tenant-aware navigation for Start Activity
// - Backward-compatible: falls back to activity templates if /api/guidelines is empty
//
// Apr 2026 alignment (phase 1, no regression):
// - Reposition this page as a reference/library surface, not the primary launch hub
// - Keep template handoff intact via Activity Launcher
// - Add shortcuts back to Activity Launcher and Comprehensive Services

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Card, CardContent, CardActions, Button, Chip, Typography, CircularProgress
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import http from '../../services/http';
import { activityApi } from '../../services/activityApi';
import GuidelineSearchBar from './components/GuidelineSearchBar';

// ---- tenant helpers ----------------------------------------------------------
function getTenantSlug() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/([^/]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  } catch { return ''; }
}
function tenantUrl(p) {
  const slug = getTenantSlug();
  const path = String(p || '/');
  if (!slug) return path;
  if (!path.startsWith('/')) return `/t/${slug}/${path}`.replace(/\/{2,}/g, '/');
  if (path.startsWith(`/t/${slug}`)) return path;
  return `/t/${slug}${path}`.replace(/\/{2,}/g, '/');
}

// ---- state helpers -----------------------------------------------------------
function getActiveCitizen() {
  try { return JSON.parse(localStorage.getItem('activeCitizen') || 'null') || null; }
  catch { return null; }
}

// Normalize records from either source (backend guidelines OR activity templates)
function normalizeItems(raw) {
  const arr = Array.isArray(raw) ? raw : (Array.isArray(raw?.items) ? raw.items : []);
  return (arr || [])
    .map((t, i) => {
      const id = (t._id || t.id || `g-${i}`).toString();
      const code = String(t.code || t.slug || id).trim();
      return {
        id,
        code,
        title: String(t.title || t.name || '').trim(),
        category: String(t.category || t.type || 'Uncategorized').trim(),
        tags: Array.isArray(t.tags) ? t.tags : (Array.isArray(t.keywords) ? t.keywords : []),
        goals: Array.isArray(t.goals) ? t.goals : [],
        steps: Array.isArray(t.steps) ? t.steps : [],
        standardsRefs: Array.isArray(t.standardsRefs) ? t.standardsRefs : [],
        attachments: Array.isArray(t.attachments) ? t.attachments : [],
        _raw: t,
      };
    })
    .filter(x => x.title);
}

export default function GuidelinesLibrary() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  // search/filter state
  const [query, setQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);

  const [activeCitizen] = useState(getActiveCitizen());
  const location = useLocation();
  const navigate = useNavigate();

  // For ?open=GL-... focusing
  const [openCode, setOpenCode] = useState('');
  const cardRefs = useRef({}); // code/id -> ref

  // Load from backend; fallback to activity templates to avoid regression
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr('');

        // 1) Try backend guidelines
        try {
          const res = await http.get('/api/guidelines?active=true&limit=500');
          const norm = normalizeItems(res?.data);
          if (norm.length) { setItems(norm); setLoading(false); return; }
        } catch {}

        // 2) Fallback to activity templates (prior flow)
        const res2 = await activityApi.listTemplates();
        const norm2 = normalizeItems(res2);
        setItems(norm2);
      } catch (e) {
        setErr(e?.message || 'Failed to load guidelines');
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Sync ?q and ?open from URL once
  useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search || '');
      const fromQ = sp.get('q') || '';
      const open = sp.get('open') || '';
      if (fromQ) setQuery(fromQ);
      if (open) setOpenCode(open);
    } catch {}
  }, [location.search]);

  // Compute filter facets
  const allCategories = useMemo(() => {
    const s = new Set(items.map(x => x.category).filter(Boolean));
    return Array.from(s).sort();
  }, [items]);

  const allTags = useMemo(() => {
    const s = new Set();
    items.forEach(x => (x.tags || []).forEach(t => t && s.add(t)));
    return Array.from(s).sort();
  }, [items]);

  // Which item matches ?open=
  const openTarget = useMemo(() => {
    if (!openCode) return null;
    const key = openCode.trim().toLowerCase();
    return (
      items.find(t => t.code.toLowerCase() === key) ||
      items.find(t => t.id.toLowerCase() === key) ||
      null
    );
  }, [openCode, items]);

  // Apply search + filters, and float the openTarget to the top
  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    const base = items.filter(t => {
      const inQ = !q || [t.title, t.category, t.code, ...(t.goals || []), ...(t.tags || [])]
        .filter(Boolean)
        .some(s => String(s).toLowerCase().includes(q));
      const inCat = !categories.length || categories.includes(t.category);
      const inTags = !tags.length || (t.tags || []).some(tag => tags.includes(tag));
      return inQ && inCat && inTags;
    });

    if (!openTarget) return base;

    // Move openTarget to first position
    const rest = base.filter(t =>
      !(t.code === openTarget.code || t.id === openTarget.id)
    );
    return [openTarget, ...rest];
  }, [items, query, categories, tags, openTarget]);

  // After render, if we have an openTarget, scroll it into view once
  useEffect(() => {
    if (!openTarget) return;
    const ref = cardRefs.current[openTarget.code] || cardRefs.current[openTarget.id];
    if (ref && ref.scrollIntoView) {
      setTimeout(() => ref.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [openTarget]);

  const goActivityLauncher = () => {
    navigate(tenantUrl('/intern/activity/new'));
  };

  const goComprehensiveServices = () => {
    navigate(tenantUrl('/dashboard/operations'));
  };

  // Launch Start Activity (keeps tenant slug)
  const applyTemplate = (tpl) => {
    const qstr = tpl?.title ? `?q=${encodeURIComponent(tpl.title)}` : '';
    navigate(tenantUrl(`/intern/activity/new${qstr}`));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <Typography variant="h6" className="font-semibold">Guidelines &amp; Reference Library</Typography>
          <Typography variant="body2" className="opacity-80">
            Use this page to review guidelines, checklists, and template references. Start new guided activities from Activity Launcher, and use Comprehensive Services for citizen selection and OPD workflow.
          </Typography>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="small" variant="outlined" onClick={goActivityLauncher}>
            Activity Launcher
          </Button>
          <Button size="small" variant="outlined" onClick={goComprehensiveServices}>
            Comprehensive Services
          </Button>
          {activeCitizen?.citizenId ? (
            <Chip size="small" variant="outlined" label={`Citizen: ${activeCitizen.name || activeCitizen.citizenId}`} />
          ) : null}
        </div>
      </div>

      {err && <Alert severity="error">{err}</Alert>}
      {openTarget && (
        <Alert severity="info">
          Opened guideline: <b>{openTarget.title}</b> ({openTarget.code})
        </Alert>
      )}

      <GuidelineSearchBar
        query={query} setQuery={setQuery}
        categories={categories} setCategories={setCategories}
        tags={tags} setTags={setTags}
        allCategories={allCategories}
        allTags={allTags}
        onClear={() => { setQuery(''); setCategories([]); setTags([]); }}
      />

      {loading ? (
        <div className="flex items-center gap-2 opacity-80">
          <CircularProgress size={18} />
          <span>Loading…</span>
        </div>
      ) : filtered.length === 0 ? (
        <Alert severity="info">No items match your filters.</Alert>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((t) => (
            <Card
              key={t.id}
              ref={el => {
                cardRefs.current[t.code] = el;
                cardRefs.current[t.id] = el;
              }}
              className="dark:bg-[#232326] dark:text-white"
              style={openTarget && (t.id === openTarget.id) ? { outline: '2px solid #4f46e5' } : {}}
            >
              <CardContent>
                <div className="text-lg font-semibold">{t.title}</div>

                <div className="mt-1 flex flex-wrap gap-1">
                  {t.category && <Chip size="small" label={t.category} />}
                  {(t.tags || []).slice(0, 4).map(tag => (
                    <Chip size="small" key={tag} label={`#${tag}`} variant="outlined" />
                  ))}
                </div>

                {Array.isArray(t.goals) && t.goals.length > 0 && (
                  <div className="mt-3">
                    <div className="text-sm font-medium">Learning goals</div>
                    <ul className="list-disc ml-5 text-sm opacity-90">
                      {t.goals.map((g, i) => <li key={i}>{g}</li>)}
                    </ul>
                  </div>
                )}

                {Array.isArray(t.standardsRefs) && t.standardsRefs.length > 0 && (
                  <div className="mt-3">
                    <div className="text-sm font-medium">Standards & references</div>
                    <ul className="list-disc ml-5 text-sm opacity-90">
                      {t.standardsRefs.map((r, i) => (
                        <li key={i}>
                          {r.url ? (
                            <a className="underline" href={r.url} target="_blank" rel="noreferrer">
                              {r.label || r.url}
                            </a>
                          ) : (r.label || '')}
                          {r.note ? ` — ${r.note}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {Array.isArray(t.steps) && t.steps.length > 0 && (
                  <div className="mt-3">
                    <div className="text-sm font-medium">Checklist (summary)</div>
                    <ol className="list-decimal ml-5 text-sm opacity-90">
                      {t.steps.slice().sort((a, b) => (a.order||0)-(b.order||0)).map((s, i) => (
                        <li key={s.order || i}>
                          <b>{s.title}</b>{s.description ? ` — ${s.description}` : ''}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {Array.isArray(t.attachments) && t.attachments.length > 0 && (
                  <div className="mt-3">
                    <div className="text-sm font-medium">Attachments</div>
                    <ul className="list-disc ml-5 text-sm opacity-90">
                      {t.attachments.map((a, i) => (
                        <li key={i}>
                          <a className="underline" href={a.url} target="_blank" rel="noreferrer">
                            {a.label || a.name || a.url}
                          </a>
                          {a.note ? ` — ${a.note}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>

              <CardActions>
                <Button size="small" onClick={() => applyTemplate(t)}>Open in Activity Launcher</Button>
              </CardActions>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
